import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert, Modal } from 'react-bootstrap';
import { FaDownload, FaPrint, FaChartBar, FaFilter, FaSearch, FaCalendarAlt, FaUserCog, FaProjectDiagram, FaClock, FaPlus } from 'react-icons/fa';
import reportService from '../services/reportService';
import projectService from '../services/projectService';
import branchService from '../services/branchService';
import overtimeService from '../services/overtimeService';
import userService from '../services/userService';
import reportHeaderPng1 from '../assets/report-headers/1.png';
import reportHeaderPng2 from '../assets/report-headers/2.png';
import electronicStamp from '../assets/report-headers/3.png';
import { formatDate, formatDateShort, formatDateTime, formatDateDayMonth, formatNumber } from '../utils/format';
import { translateIssueType, translateIssueTypes } from '../utils/issueTypes';
import { normalizeRole } from '../utils/rbac';

// Custom style for large numbers in reports
const largeNumberStyle = {
  fontSize: '1.2rem',
  fontWeight: 'bold',
  padding: '0.4rem 0.6rem'
};

// Arabic month names
const getMonthName = (monthNum) => {
  const months = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];
  return months[parseInt(monthNum, 10) - 1] || '';
};

// Normalize numeric values coming from localized UI (Arabic digits, thousands separators, etc.)
const normalizeLocaleInteger = (value) => {
  if (value === null || value === undefined) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  const arabicIndicDigits = '٠١٢٣٤٥٦٧٨٩';
  const easternArabicIndicDigits = '۰۱۲۳۴۵۶۷۸۹';

  const normalized = raw
    .split('')
    .map((ch) => {
      const arIdx = arabicIndicDigits.indexOf(ch);
      if (arIdx !== -1) return String(arIdx);
      const faIdx = easternArabicIndicDigits.indexOf(ch);
      if (faIdx !== -1) return String(faIdx);
      return ch;
    })
    .join('')
    .replace(/[\u066B\u066C,\s]/g, '') // Arabic decimal/thousands separators, comma, spaces
    .replace(/\.(?=\d{3}(\D|$))/g, ''); // Remove dot as thousands separator when applicable

  const parsed = parseInt(normalized, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const Reports = () => {
  const authRaw = localStorage.getItem('user');
  let currentUser = null;
  try {
    currentUser = authRaw ? JSON.parse(authRaw) : null;
  } catch {
    currentUser = null;
  }

  const currentRole = normalizeRole(currentUser?.role);

  const [activeTab, setActiveTab] = useState('weekly');
  const [weeklyStart, setWeeklyStart] = useState('');
  const [weeklyEnd, setWeeklyEnd] = useState('');
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear().toString());
  const [monthlyMonth, setMonthlyMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [monthlyData, setMonthlyData] = useState(null);
  const [techYear, setTechYear] = useState(new Date().getFullYear().toString());
  const [techMonth, setTechMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [techData, setTechData] = useState(null);
  // Project report states
  const [projectStart, setProjectStart] = useState('');
  const [projectEnd, setProjectEnd] = useState('');
  const [projectData, setProjectData] = useState(null);
  // Overtime report states
  const [overtimeYear, setOvertimeYear] = useState(new Date().getFullYear().toString());
  const [overtimeMonth, setOvertimeMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [overtimeData, setOvertimeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Standalone Overtime states
  const [showOvertimeModal, setShowOvertimeModal] = useState(false);
  const [branches, setBranches] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [overtimeForm, setOvertimeForm] = useState({
    branch_id: '',
    description: '',
    overtime_type: 'after_hours',
    overtime_duration: '',
    overtime_date: new Date().toISOString().split('T')[0],
    technician_ids: []
  });
  const [isEditingOvertime, setIsEditingOvertime] = useState(false);
  const [editOvertimeId, setEditOvertimeId] = useState(null);
  const generatedAt = formatDateTime(new Date());
  const hasRenderedReport = Boolean(weeklyData || monthlyData || techData || projectData || overtimeData);

  useEffect(() => {
    const today = new Date();
    const lastWeek = new Date();
    lastWeek.setDate(today.getDate() - 7);
    setWeeklyEnd(today.toISOString().slice(0, 10));
    setWeeklyStart(lastWeek.toISOString().slice(0, 10));
    
    // Load data for standalone overtime modal
    fetchBranches();
    fetchAssignableUsers();
  }, []);

  const fetchBranches = async () => {
    try {
      const data = await branchService.getAll();
      setBranches(data.branches || data || []);
    } catch (err) {
      console.error('Failed to load branches', err);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const data = await userService.getTicketAssignableUsers();
      setAssignableUsers(data.users || data || []);
    } catch (err) {
      console.error('Failed to load users', err);
    }
  };

  const handleTechToggle = (userId) => {
    setOvertimeForm(prev => {
      const ids = prev.technician_ids.includes(userId)
        ? prev.technician_ids.filter(id => id !== userId)
        : [...prev.technician_ids, userId];
      return { ...prev, technician_ids: ids };
    });
  };

  const handleSaveOvertime = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditingOvertime) {
        await overtimeService.update(editOvertimeId, overtimeForm);
        alert('تم تحديث العمل الإضافي بنجاح');
      } else {
        await overtimeService.create(overtimeForm);
        alert('تم إضافة العمل الإضافي بنجاح');
      }
      
      setShowOvertimeModal(false);
      setIsEditingOvertime(false);
      setEditOvertimeId(null);
      setOvertimeForm({
        branch_id: '',
        description: '',
        overtime_type: 'after_hours',
        overtime_duration: '',
        overtime_date: new Date().toISOString().split('T')[0],
        technician_ids: []
      });
      // Refresh report if currently viewing overtime
      if (activeTab === 'overtime') {
        handleOvertimeReport(e);
      }
    } catch (err) {
      console.error(err);
      setError('فشل حفظ العمل الإضافي');
    } finally {
      setLoading(false);
    }
  };

  const handleEditOvertime = (record) => {
    setOvertimeForm({
      branch_id: record.branch_id || '',
      description: record.subject,
      overtime_type: record.overtime_type,
      overtime_duration: record.overtime_duration || '',
      overtime_date: record.received_date ? new Date(record.received_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      technician_ids: [] // We'll need to fetch or parse these if possible, but for now we'll let user re-select or keep as is
    });
    
    // Note: If technician_ids are needed, we'd need them from the backend record.
    // The technician_names is a string, so we can't easily map back to IDs without more data.
    // However, if the user just wants to edit text/date/type, this works.
    
    setEditOvertimeId(record.id);
    setIsEditingOvertime(true);
    setShowOvertimeModal(true);
  };

  const handleDeleteOvertime = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا السجل؟')) return;
    
    setLoading(true);
    try {
      await overtimeService.delete(id);
      alert('تم حذف السجل بنجاح');
      // Refresh report
      const year = parseInt(overtimeYear, 10);
      const month = parseInt(overtimeMonth, 10);
      const startStr = `${year}-${String(month).padStart(2, '0')}-01`;
      const endStr = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
      const data = await reportService.getOvertime(startStr, endStr);
      setOvertimeData(data);
    } catch (err) {
      console.error(err);
      setError('فشل حذف السجل');
    } finally {
      setLoading(false);
    }
  };

  const handleWeeklyReport = async (e) => {
    e.preventDefault();
    if (!weeklyStart || !weeklyEnd) return;
    setLoading(true);
    setError('');
    try {
      const data = await reportService.getWeekly(weeklyStart, weeklyEnd);
      setWeeklyData(data);
    } catch (err) {
      console.error(err);
      setError('فشل تحميل التقرير الأسبوعي');
    } finally {
      setLoading(false);
    }
  };

  const handleMonthlyReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await reportService.getMonthly(monthlyYear, monthlyMonth);
      setMonthlyData(data);
    } catch (err) {
      console.error(err);
      setError('فشل تحميل التقرير الشهري');
    } finally {
      setLoading(false);
    }
  };

  const handleTechReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const year = parseInt(techYear, 10);
      const month = parseInt(techMonth, 10);
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0);
      
      const startStr = `${year}-${String(month).padStart(2, '0')}-01`;
      const endStr = `${year}-${String(month).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
      
      const data = await reportService.getTechnicianPerformance(startStr, endStr);
      setTechData(data);
    } catch (err) {
      console.error(err);
      setError('فشل تحميل تقرير أداء الفنيين');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await projectService.getProjects({
        start_date: projectStart,
        end_date: projectEnd,
        status: '',
        project_type: ''
      });
      setProjectData(data);
    } catch (err) {
      console.error(err);
      setError('فشل تحميل تقرير المشاريع');
    } finally {
      setLoading(false);
    }
  };

  const handleOvertimeReport = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const year = parseInt(overtimeYear, 10);
      const month = parseInt(overtimeMonth, 10);
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0);
      
      const startStr = `${year}-${String(month).padStart(2, '0')}-01`;
      const endStr = `${year}-${String(month).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;

      const data = await reportService.getOvertime(startStr, endStr);
      setOvertimeData(data);
    } catch (err) {
      console.error(err);
      setError('فشل تحميل تقرير العمل الإضافي');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const renderReportHeader = (title = 'تقرير رسمي', meta = '') => {
    return (
      <div className="report-print-header">
        <div className="report-header">
          {/* Logo 2 (png2) - positioned on the RIGHT side */}
          <div className="report-header-side report-header-brand-mark report-header-side-right">
            <img
              src={reportHeaderPng2}
              alt="شعار الجهة - يمين"
              className="report-header-logo"
            />
          </div>
          {/* Center branding block */}
          <div className="report-header-center">
            <div className="report-branding">
              <div className="report-subtitle">البنية التحتية للاتصالات والتقنية</div>
              <div className="report-system">نظام الدعم الفني</div>
            </div>
            <div className="report-title">{title}</div>
            {meta ? <div className="report-header-meta">{meta}</div> : null}
          </div>
          {/* Logo 1 (png1) - positioned on the LEFT side */}
          <div className="report-header-side report-header-brand-mark report-header-side-left">
            <img
              src={reportHeaderPng1}
              alt="شعار الجهة - يسار"
              className="report-header-logo"
            />
          </div>
        </div>
        <div className="report-header-separator" aria-hidden="true"></div>
      </div>
    );
  };

  const renderFormalMessage = (reportType, month = null, year = null, periodText = '') => {
    const monthName = month ? getMonthName(month) : '';
    const yearText = year || new Date().getFullYear();

    let titleText = '';
    let bodyText = '';

    switch (reportType) {
      case 'monthly':
        titleText = `تقرير شهري بخصوص المهام والاستدعاءات الخاصة بالمصرف - ${monthName} ${yearText}`;
        bodyText = `تفضلوا باستلام التقرير الشهري الذي يوضح أغلب المهام والاستدعاءات الواردة والمستلمة إما عبر البريد الإلكتروني، واتس اب، أو عبر الهاتف أو شخصياً وذلك عن شهر ${monthName} ${yearText}`;
        break;
      case 'weekly':
        titleText = `تقرير أسبوعي بخصوص المهام والاستدعاءات - الفترة: ${periodText}`;
        bodyText = `تفضلوا باستلام التقرير الأسبوعي الذي يوضح أغلب المهام والاستدعاءات الواردة والمستلمة إما عبر البريد الإلكتروني، واتس اب، أو عبر الهاتف أو شخصياً وذلك للفترة ${periodText}`;
        break;
      case 'technician':
        titleText = periodText && periodText !== 'كل الفترات'
          ? `تقرير أداء الفنيين - الفترة: ${periodText}`
          : 'تقرير أداء الفنيين - جميع الفترات';
        bodyText = periodText && periodText !== 'كل الفترات'
          ? `تفضلوا باستلام تقرير أداء الفنيين للفترة ${periodText} والذي يوضح إحصائيات الأداء ونسب الإنجاز والمهام المنجزة`
          : `تفضلوا باستلام تقرير أداء الفنيين لجميع الفترات والذي يوضح إحصائيات الأداء ونسب الإنجاز والمهام المنجزة`;
        break;
      case 'project':
        titleText = periodText && periodText !== 'كل الفترات'
          ? `تقرير المشاريع - الفترة: ${periodText}`
          : 'تقرير المشاريع - جميع الفترات';
        bodyText = periodText && periodText !== 'كل الفترات'
          ? `تفضلوا باستلام تقرير المشاريع للفترة ${periodText} والذي يوضح حالة المشاريع ونسب الإنجاز والتقدم المحرز`
          : `تفضلوا باستلام تقرير المشاريع لجميع الفترات والذي يوضح حالة المشاريع ونسب الإنجاز والتقدم المحرز`;
        break;
      case 'overtime':
        titleText = periodText && periodText !== 'كل الفترات'
          ? `تقرير العمل الإضافي - الفترة: ${periodText}`
          : 'تقرير العمل الإضافي - جميع الفترات';
        bodyText = periodText && periodText !== 'كل الفترات'
          ? `تفضلوا باستلام تقرير العمل الإضافي للفترة ${periodText} والذي يوضح التذاكر التي تم العمل عليها خارج أوقات العمل الرسمية`
          : `تفضلوا باستلام تقرير العمل الإضافي لجميع الفترات والذي يوضح التذاكر التي تم العمل عليها خارج أوقات العمل الرسمية`;
        break;
      default:
        titleText = 'تقرير بخصوص المهام والاستدعاءات الخاصة بالمصرف';
        bodyText = 'تفضلوا باستلام التقرير الذي يوضح أغلب المهام والاستدعاءات الواردة والمستلمة';
    }

    return (
      <div className="report-formal-message">
        <div className="formal-salutation">
          {['overtime', 'technician'].includes(reportType) ? 'السيد / المدير المالي' : 'السادة مصرف شمال أفريقيا'}
        </div>
        <div className="formal-greeting">تحية طيبة</div>
        <div className="formal-subject">الموضوع: {titleText}</div>
        <div className="formal-body">{bodyText}</div>
      </div>
    );
  };

  const renderReportConclusion = () => {
    return (
      <div className="report-conclusion">
        <div className="report-notes-section">
          <div className="report-notes-title">الملاحظات:</div>
          <div className="report-notes-lines">
            <div className="report-note-line"></div>
            <div className="report-note-line"></div>
            <div className="report-note-line"></div>
            <div className="report-note-line"></div>
            <div className="report-note-line"></div>
          </div>
        </div>
        <div className="report-signatures">
          <div className="report-signature-box approval-single">
            <div className="signature-label">اعتماد المشرف والتوقيع</div>
            <div className="approval-stamp-container">
              <img
                src={electronicStamp}
                alt="ختم الإدارة"
                className="approval-stamp-image"
              />
            </div>
            <div className="signature-line"></div>
          </div>
        </div>
      </div>
    );
  };

  const getTicketDate = (ticket) => (
    formatDateShort(
      ticket?.received_date ||
      ticket?.ticket_date ||
      ticket?.ticket_created_at ||
      ticket?.created_at
    )
  );

  // Get ticket date in day + month name format (e.g., "14 March", "7 February")
  const getTicketDateFormatted = (ticket) => {
    const dateValue = ticket?.received_date || ticket?.ticket_date || ticket?.ticket_created_at || ticket?.created_at;
    return formatDateDayMonth(dateValue, 'ar');
  };

  const getTicketIssueTypeLabel = (ticket) => {
    if (Array.isArray(ticket?.issue_types) && ticket.issue_types.length > 0) {
      return translateIssueTypes(ticket.issue_types, '-');
    }

    return translateIssueType(ticket?.issue_type, '-');
  };

  const getTicketStatusLabel = (status) => {
    const labels = {
      new: 'جديدة',
      assigned: 'تم الإسناد',
      in_progress: 'قيد العمل',
      on_hold: 'معلقة',
      pending: 'معلقة',
      resolved: 'محلولة',
      closed: 'مغلقة'
    };

    return labels[String(status || '').toLowerCase()] || status || '-';
  };

  const renderSummaryCards = (stats) => {
    const cards = [
      { key: 'total', className: 'total', value: stats.total, label: 'إجمالي التذاكر' },
      { key: 'new', className: 'new', value: stats.new, label: 'جديدة' },
      { key: 'in-progress', className: 'inprogress', value: stats.in_progress, label: 'قيد العمل' },
      { key: 'resolved', className: 'resolved', value: stats.resolved, label: 'المحلولة' }
    ];

    return (
      <div className="report-summary-cards">
        {cards.map((card) => (
          <div key={card.key} className={`summary-card ${card.className}`}>
            <div className="summary-number">{formatNumber(card.value)}</div>
            <div className="summary-label">{card.label}</div>
          </div>
        ))}
      </div>
    );
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  const renderWeekly = () => {
    if (!weeklyData) return null;
    const { tickets, period, stats, topIssues, topBranches, unresolvedTickets } = weeklyData;
    const periodText = `${period.start_date} إلى ${period.end_date}`;
    const resolvedTickets = tickets || [];

    return (
      <div className="report-print-area report-page">
        {renderReportHeader('تقرير أسبوعي (التذاكر)', periodText)}
        {renderFormalMessage('weekly', null, null, periodText)}

        {renderSummaryCards(stats)}

        {/* Side by Side Summary Tables */}
        {(topIssues?.length > 0 || topBranches?.length > 0) && (
          <div className="report-summary-row">
            {topIssues && topIssues.length > 0 && (
              <div className="report-section summary-half">
                <div className="section-header">
                  <div className="section-title">أنواع التذاكر الأكثر شيوعاً</div>
                </div>
                <div className="report-table-container">
                  <table className="report-data-table compact">
                    <thead>
                      <tr>
                        <th className="col-num">#</th>
                        <th className="col-issue">النوع</th>
                        <th className="col-count">العدد</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topIssues.map((issue, idx) => (
                        <tr key={idx}>
                          <td className="cell-num">{formatNumber(idx + 1)}</td>
                          <td className="cell-issue">{translateIssueType(issue.issue_type)}</td>
                          <td className="cell-count"><span className="number-badge primary">{issue.count}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {topBranches && topBranches.length > 0 && (
              <div className="report-section summary-half">
                <div className="section-header">
                  <div className="section-title">أكثر الفروع طلباً</div>
                </div>
                <div className="report-table-container">
                  <table className="report-data-table compact">
                    <thead>
                      <tr>
                        <th className="col-num">#</th>
                        <th className="col-branch">الفرع</th>
                        <th className="col-count">العدد</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topBranches.map((branch, idx) => (
                        <tr key={idx}>
                          <td className="cell-num">{formatNumber(idx + 1)}</td>
                          <td className="cell-branch">{branch.name}</td>
                          <td className="cell-count"><span className="number-badge secondary">{branch.ticket_count}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Unresolved Tickets Section - includes New, Pending, In Progress, On Hold */}
        {unresolvedTickets && unresolvedTickets.length > 0 && (
          <div className="report-section">
            <div className="section-header">
              <div className="section-title">التذاكر غير المحلولة <span className="section-count">({unresolvedTickets.length})</span></div>
            </div>
            <div className="report-table-container">
              <table className="report-data-table ticket-report-table ticket-report-table--unresolved responsive-data-table">
                <thead>
                  <tr>
                    <th className="col-num">#</th>
                    <th className="col-ticket-date">تاريخ التذكرة</th>
                    <th className="col-subject">الموضوع</th>
                    <th className="col-branch">الفرع</th>
                    <th className="col-type">نوع المشكلة</th>
                    <th className="col-status">الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {unresolvedTickets.map((ticket, idx) => (
                    <tr key={idx}>
                      <td className="cell-num" data-label="#">{formatNumber(idx + 1)}</td>
                      <td className="cell-ticket-date" data-label="تاريخ التذكرة">
                        <span className="ticket-date-value">{getTicketDateFormatted(ticket)}</span>
                      </td>
                      <td className="cell-subject" data-label="الموضوع">{ticket.short_description}</td>
                      <td className="cell-branch" data-label="الفرع">{ticket.branch_name || '-'}</td>
                      <td className="cell-type" data-label="نوع المشكلة">{getTicketIssueTypeLabel(ticket)}</td>
                      <td className="cell-status" data-label="الحالة">{getTicketStatusLabel(ticket.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Resolved Tickets Section */}
        <div className="report-section">
          <div className="section-header">
            <div className="section-title">التذاكر المحلولة مع الحلول <span className="section-count">({resolvedTickets.length})</span></div>
          </div>
          <div className="report-table-container">
            <table className="report-data-table ticket-report-table ticket-report-table--resolved responsive-data-table">
              <thead>
                <tr>
                  <th className="col-num">#</th>
                  <th className="col-ticket-date">تاريخ التذكرة</th>
                  <th className="col-subject">الموضوع</th>
                  <th className="col-branch">الفرع</th>
                  <th className="col-type">نوع المشكلة</th>
                  <th className="col-resolution">ملخص الحل</th>
                </tr>
              </thead>
              <tbody>
                {resolvedTickets.map((ticket, idx) => (
                  <tr key={ticket.id}>
                    <td className="cell-num" data-label="#">{formatNumber(idx + 1)}</td>
                    <td className="cell-ticket-date" data-label="تاريخ التذكرة">
                      <span className="ticket-date-value">{getTicketDateFormatted(ticket)}</span>
                    </td>
                    <td className="cell-subject" data-label="الموضوع">{ticket.subject}</td>
                    <td className="cell-branch" data-label="الفرع">{ticket.branch_name || '-'}</td>
                    <td className="cell-type" data-label="نوع المشكلة">{getTicketIssueTypeLabel(ticket)}</td>
                    <td className="cell-resolution" data-label="ملخص الحل">{ticket.resolution_notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {renderReportConclusion()}

        <div className="report-page-footer">
          <span>وقت الطباعة: {generatedAt}</span>
          <span className="report-page-number" />
          <span>نظام التقارير - البنية التحتية للاتصالات والتقنية</span>
        </div>
      </div>
    );
  };

  const renderMonthly = () => {
    if (!monthlyData) return null;
    const { tickets, period, stats, topIssues, topBranches, unresolvedTickets } = monthlyData;
    const resolvedTickets = tickets || [];

    return (
      <div className="report-print-area report-page">
        {renderReportHeader('تقرير شهري (التذاكر)', `${getMonthName(monthlyMonth)} ${monthlyYear}`)}
        {renderFormalMessage('monthly', monthlyMonth, monthlyYear)}

        {renderSummaryCards(stats)}

        {/* Side by Side Summary Tables */}
        {(topIssues?.length > 0 || topBranches?.length > 0) && (
          <div className="report-summary-row">
            {topIssues && topIssues.length > 0 && (
              <div className="report-section summary-half">
                <div className="section-header">
                  <div className="section-title">أنواع التذاكر الأكثر شيوعاً</div>
                </div>
                <div className="report-table-container">
                  <table className="report-data-table compact">
                    <thead>
                      <tr>
                        <th className="col-num">#</th>
                        <th className="col-issue">النوع</th>
                        <th className="col-count">العدد</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topIssues.map((issue, idx) => (
                        <tr key={idx}>
                          <td className="cell-num">{formatNumber(idx + 1)}</td>
                          <td className="cell-issue">{translateIssueType(issue.issue_type)}</td>
                          <td className="cell-count"><span className="number-badge primary">{issue.count}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {topBranches && topBranches.length > 0 && (
              <div className="report-section summary-half">
                <div className="section-header">
                  <div className="section-title">أكثر الفروع طلباً</div>
                </div>
                <div className="report-table-container">
                  <table className="report-data-table compact">
                    <thead>
                      <tr>
                        <th className="col-num">#</th>
                        <th className="col-branch">الفرع</th>
                        <th className="col-count">العدد</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topBranches.map((branch, idx) => (
                        <tr key={idx}>
                          <td className="cell-num">{formatNumber(idx + 1)}</td>
                          <td className="cell-branch">{branch.name}</td>
                          <td className="cell-count"><span className="number-badge secondary">{branch.ticket_count}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Resolved Tickets Section - Now first */}
        <div className="report-section">
          <div className="section-header">
            <div className="section-title">التذاكر المحلولة مع الحلول <span className="section-count">({resolvedTickets.length})</span></div>
          </div>
          <div className="report-table-container">
            <table className="report-data-table ticket-report-table ticket-report-table--resolved responsive-data-table">
              <thead>
                <tr>
                  <th className="col-num">#</th>
                  <th className="col-ticket-date">تاريخ التذكرة</th>
                  <th className="col-subject">الموضوع</th>
                  <th className="col-branch">الفرع</th>
                  <th className="col-resolution">ملخص الحل</th>
                </tr>
              </thead>
              <tbody>
                {resolvedTickets.map((ticket, idx) => (
                  <tr key={ticket.id}>
                    <td className="cell-num" data-label="#">{formatNumber(idx + 1)}</td>
                    <td className="cell-ticket-date" data-label="تاريخ التذكرة">
                      <span className="ticket-date-value">{getTicketDateFormatted(ticket)}</span>
                    </td>
                    <td className="cell-subject" data-label="الموضوع">{ticket.subject}</td>
                    <td className="cell-branch" data-label="الفرع">{ticket.branch_name || '-'}</td>
                    <td className="cell-resolution" data-label="ملخص الحل">{ticket.resolution_notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Unresolved Tickets Section - Now starts from Page 2 */}
        {unresolvedTickets && unresolvedTickets.length > 0 && (
          <div className="report-section" style={{ pageBreakBefore: 'always', marginTop: '20px' }}>
            <div className="section-header">
              <div className="section-title">التذاكر غير المحلولة <span className="section-count">({unresolvedTickets.length})</span></div>
            </div>
            <div className="report-table-container">
              <table className="report-data-table ticket-report-table ticket-report-table--unresolved responsive-data-table">
                <thead>
                  <tr>
                    <th className="col-num">#</th>
                    <th className="col-ticket-date">تاريخ التذكرة</th>
                    <th className="col-subject">الموضوع</th>
                    <th className="col-branch">الفرع</th>
                    <th className="col-status">الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {unresolvedTickets.map((ticket, idx) => (
                    <tr key={idx}>
                      <td className="cell-num" data-label="#">{formatNumber(idx + 1)}</td>
                      <td className="cell-ticket-date" data-label="تاريخ التذكرة">
                        <span className="ticket-date-value">{getTicketDateFormatted(ticket)}</span>
                      </td>
                      <td className="cell-subject" data-label="الموضوع">{ticket.short_description}</td>
                      <td className="cell-branch" data-label="الفرع">{ticket.branch_name || '-'}</td>
                      <td className="cell-status" data-label="الحالة">{getTicketStatusLabel(ticket.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {renderReportConclusion()}

        <div className="report-page-footer">
          <span>وقت الطباعة: {generatedAt}</span>
          <span className="report-page-number" />
          <span>نظام التقارير - البنية التحتية للاتصالات والتقنية</span>
        </div>
      </div>
    );
  };

  const renderTech = () => {
    if (!techData) return null;
    const { technicians, period } = techData;
    
    const getTechPeriodText = () => {
      if (!period || typeof period === 'string' || !period.start_date) return 'جميع الفترات';
      const date = new Date(period.start_date);
      const monthName = getMonthName(date.getMonth() + 1);
      const year = date.getFullYear();
      return `${monthName} ${formatNumber(year)}`;
    };

    const periodText = getTechPeriodText();

    return (
      <div className="report-print-area report-page report-page--landscape text-center center-aligned-report">
        {renderReportHeader('تقرير أداء الفنيين', periodText)}
        {renderFormalMessage('technician', null, null, periodText)}

        <div className="report-section">
          <div className="section-header">
            <div className="section-title">تقرير أداء الفنيين</div>
          </div>
          <div className="report-table-container">
            <table className="report-data-table tech-performance-table">
              <thead>
                <tr>
                  <th className="col-tech-name">الفني</th>
                  <th className="col-tech-total">إجمالي التذاكر</th>
                  <th className="col-tech-resolved">المحلولة</th>
                  <th className="col-tech-rate">نسبة الحل (%)</th>
                  <th className="col-tech-rating">التقييم</th>
                  <th className="col-tech-exceptional">أعمال استثنائية</th>
                </tr>
              </thead>
              <tbody>
                {technicians.map((t) => (
                  <tr key={t.id}>
                    <td className="cell-tech-name">{t.full_name}</td>
                    <td className="cell-tech-total"><span className="number-badge">{formatNumber(t.total_assigned)}</span></td>
                    <td className="cell-tech-resolved"><span className="number-badge success">{formatNumber(t.resolved_count)}</span></td>
                    <td className="cell-tech-rate">{formatNumber(t.resolution_rate)}%</td>
                    <td className="cell-tech-rating">
                      {t.avg_rating ? (
                        <span className="rating-display">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span key={star} style={{ color: star <= Math.round(t.avg_rating) ? '#ffc107' : '#ddd', fontSize: '0.9rem' }}>★</span>
                          ))}
                          <span className="ms-1 text-muted" style={{ fontSize: '0.75rem' }}>({t.rated_tickets_count || 0})</span>
                        </span>
                      ) : '-'}
                    </td>
                    <td className="cell-tech-exceptional">
                      {t.exceptional_work_count > 0 ? (
                        <span className="badge bg-warning">{t.exceptional_work_count}</span>
                      ) : '0'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {renderReportConclusion()}

        <div className="report-page-footer">
          <span>وقت الطباعة: {generatedAt}</span>
          <span className="report-page-number" />
          <span>نظام التقارير - البنية التحتية للاتصالات والتقنية</span>
        </div>
      </div>
    );
  };

  const renderProjectReport = () => {
    if (!projectData) return null;
    const projects = projectData.projects || [];

    const getStatusBadge = (status) => {
      const badges = {
        'planning': 'info',
        'in_progress': 'primary',
        'on_hold': 'warning',
        'completed': 'success',
        'cancelled': 'danger'
      };
      const labels = {
        'planning': 'تخطيط',
        'in_progress': 'قيد التنفيذ',
        'on_hold': 'معلق',
        'completed': 'مكتمل',
        'cancelled': 'ملغى'
      };
      return <Badge bg={badges[status]} style={{ fontSize: '9px' }}>{labels[status] || status}</Badge>;
    };

    const getProjectTypeLabel = (type) => {
      const types = {
        'network': 'شبكات',
        'cameras': 'كاميرات مراقبة',
        'systems': 'أنظمة برمجية',
        'infrastructure': 'بنية تحتية',
        'other': 'أخرى'
      };
      return types[type] || type;
    };

    const periodText = projectStart && projectEnd ? `${projectStart} إلى ${projectEnd}` : 'كل الفترات';

    return (
      <div className="report-print-area report-page">
        {renderReportHeader('تقرير المشاريع', periodText)}
        {renderFormalMessage('project', null, null, periodText)}

        <div className="report-section">
          <div className="section-header">
            <div className="section-title">تقرير المشاريع <span className="section-count">({projects.length})</span></div>
          </div>
          <div className="report-table-container">
            <table className="report-data-table project-table">
              <thead>
                <tr>
                  <th className="col-project-code">كود المشروع</th>
                  <th className="col-project-name">اسم المشروع</th>
                  <th className="col-project-client">العميل</th>
                  <th className="col-project-type">النوع</th>
                  <th className="col-project-status">الحالة</th>
                  <th className="col-project-start">البداية</th>
                  <th className="col-project-end">النهاية</th>
                  <th className="col-project-budget">الميزانية</th>
                  <th className="col-project-progress">نسبة الإنجاز</th>
                </tr>
              </thead>
              <tbody>
                {projects.length > 0 ? (
                  projects.map((p) => (
                    <tr key={p.id}>
                      <td className="cell-project-code">{p.project_code}</td>
                      <td className="cell-project-name">{p.project_name}</td>
                      <td className="cell-project-client">{p.client_name || '-'}</td>
                      <td className="cell-project-type">{getProjectTypeLabel(p.project_type)}</td>
                      <td className="cell-project-status">{getStatusBadge(p.status)}</td>
                      <td className="cell-project-start">{p.start_date ? formatDate(p.start_date) : '-'}</td>
                      <td className="cell-project-end">{p.end_date ? formatDate(p.end_date) : '-'}</td>
                      <td className="cell-project-budget">{p.budget ? `${formatNumber(p.budget)} $` : '-'}</td>
                      <td className="cell-project-progress">
                        <div className="progress-cell">
                          <span className="progress-value">{formatNumber(p.completion_percentage || 0)}%</span>
                          <div className="progress-bar-mini">
                            <div className="progress-fill" style={{ width: `${p.completion_percentage || 0}%` }}></div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center empty-message">لا توجد مشاريع</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {renderReportConclusion()}

        <div className="report-page-footer">
          <span>وقت الطباعة: {generatedAt}</span>
          <span className="report-page-number" />
          <span>نظام التقارير - البنية التحتية للاتصالات والتقنية</span>
        </div>
      </div>
    );
  };

  const renderOvertimeReport = () => {
    if (!overtimeData) return null;
    const { tickets, stats, period } = overtimeData;
    const overtimeTickets = tickets || [];

    const getOvertimePeriodText = () => {
      if (!period || !period.start_date) return 'جميع الفترات';
      
      const date = new Date(period.start_date);
      const monthName = getMonthName(date.getMonth() + 1);
      const year = date.getFullYear();
      
      return `${monthName} ${formatNumber(year)}`;
    };

    const periodText = getOvertimePeriodText();

    // Helper function to translate overtime type
    const translateOvertimeType = (type) => {
      const types = {
        'after_hours': 'بعد ساعات العمل',
        'holiday': 'عطلة رسمية',
        'travel_over_100km': 'سفر أكثر من 100 كم'
      };
      return types[type] || type || '-';
    };

    const getTicketStatusLabel = (status) => {
      const labels = {
        new: 'جديدة',
        assigned: 'تم الإسناد',
        in_progress: 'قيد العمل',
        on_hold: 'معلقة',
        pending: 'معلقة',
        resolved: 'محلولة',
        closed: 'مغلقة'
      };
      return labels[String(status || '').toLowerCase()] || status || '-';
    };

    return (
      <div className="report-print-area report-page report-page--landscape text-center center-aligned-report">
        {renderReportHeader('تقرير العمل الإضافي', periodText)}
        {renderFormalMessage('overtime', null, null, periodText)}

        {/* Summary Cards */}
        <div className="report-summary-cards overtime-summary-centered">
          <div className="summary-card total centered-card">
            <div className="summary-number">{formatNumber(stats.total_overtime_tickets || 0)}</div>
            <div className="summary-label wrap-text">إجمالي التذاكر بالعمل الإضافي</div>
          </div>
          <div className="summary-card resolved centered-card">
            <div className="summary-number">{formatNumber(stats.resolved_count || 0)}</div>
            <div className="summary-label wrap-text">المحلولة</div>
          </div>
          <div className="summary-card inprogress centered-card">
            <div className="summary-number">{formatNumber(stats.open_count || 0)}</div>
            <div className="summary-label wrap-text">قيد العمل</div>
          </div>
        </div>

        <div className="report-summary-cards overtime-summary-centered">
          <div className="summary-card centered-card">
            <div className="summary-number">{formatNumber(stats.after_hours_count || 0)}</div>
            <div className="summary-label wrap-text">بعد ساعات العمل</div>
          </div>
          <div className="summary-card centered-card">
            <div className="summary-number">{formatNumber(stats.holiday_count || 0)}</div>
            <div className="summary-label wrap-text">عطلات رسمية</div>
          </div>
          <div className="summary-card centered-card">
            <div className="summary-number">{formatNumber(stats.travel_count || 0)}</div>
            <div className="summary-label wrap-text">سفر أكثر من 100 كم</div>
          </div>
        </div>

        {/* Overtime Tickets Table */}
        <div className="report-section">
          <div className="section-header">
            <div className="section-title">تذاكر العمل الإضافي <span className="section-count">({overtimeTickets.length})</span></div>
          </div>
          <div className="report-table-container">
            <table className="report-data-table ticket-report-table overtime-table responsive-data-table">
              <thead>
                <tr>
                  <th className="ot-col-num">#</th>
                  <th className="ot-col-date">تاريخ التذكرة</th>
                  <th className="ot-col-subject no-wrap-header">الموضوع</th>
                  <th className="ot-col-branch">الفرع</th>
                  <th className="ot-col-tech">بواسطة</th>
                  <th className="ot-col-ov-type">نوع العمل الإضافي</th>
                  <th className="ot-col-ov-dur">المدة</th>
                  <th className="ot-col-actions d-print-none">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {overtimeTickets.length > 0 ? (
                  overtimeTickets.map((ticket, idx) => (
                    <tr key={ticket.id}>
                      <td className="ot-cell-num" data-label="#">{formatNumber(idx + 1)}</td>
                      <td className="ot-cell-date" data-label="تاريخ التذكرة">
                        <span className="ticket-date-value">{getTicketDateFormatted(ticket)}</span>
                      </td>
                      <td className="ot-cell-subject" data-label="الموضوع">{ticket.subject}</td>
                      <td className="ot-cell-branch" data-label="الفرع">{ticket.branch_name || '-'}</td>
                      <td className="ot-cell-tech" data-label="بواسطة">{ticket.technician_names || '-'}</td>
                      <td className="ot-cell-ov-type" data-label="نوع العمل الإضافي">{translateOvertimeType(ticket.overtime_type)}</td>
                      <td className="ot-cell-ov-dur" data-label="مدة العمل الإضافي">{ticket.overtime_duration || '-'}</td>
                      <td className="ot-cell-actions d-print-none" data-label="إجراءات">
                        {ticket.source === 'standalone' && (
                          <div className="d-flex gap-1 justify-content-center">
                            <Button size="sm" variant="outline-primary" onClick={() => handleEditOvertime(ticket)}>تعديل</Button>
                            <Button size="sm" variant="outline-danger" onClick={() => handleDeleteOvertime(ticket.id)}>حذف</Button>
                          </div>
                        )}
                        {ticket.source === 'ticket' && (
                          <span className="text-muted small">تلقائي</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center empty-message">لا توجد تذاكر عمل إضافي</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {renderReportConclusion()}

        <div className="report-page-footer">
          <span>وقت الطباعة: {generatedAt}</span>
          <span className="report-page-number" />
          <span>نظام التقارير - البنية التحتية للاتصالات والتقنية</span>
        </div>
      </div>
    );
  };

  const TAB_DEFS = [
    { id: 'weekly', label: 'أسبوعي (تذاكر)', icon: <FaCalendarAlt /> },
    { id: 'monthly', label: 'شهري (تذاكر)', icon: <FaChartBar /> },
    { id: 'technician', label: 'أداء الفنيين', icon: <FaUserCog /> },
    { id: 'project', label: 'المشاريع', icon: <FaProjectDiagram /> },
    { id: 'overtime', label: 'العمل الإضافي', icon: <FaClock /> },
  ];

  const visibleTabs = TAB_DEFS;

  return (
    <div className="reports-page">
      {/* ── Page Header ── */}
      <div className="reports-header d-print-none">
        <div className="reports-header-main">
          <div className="reports-header-icon">
            <FaChartBar />
          </div>
          <div>
            <div className="reports-header-title">التقارير</div>
            <div className="reports-header-subtitle">مخرجات تشغيلية وتحليلية للإدارة</div>
          </div>
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="report-error d-print-none">
          <span>⚠</span>
          <span>{error}</span>
        </div>
      )}

      {/* ── Tab Bar ── */}
      <div className="report-tabs-bar d-print-none">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`report-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Ready Toolbar ── */}
      {hasRenderedReport && (
        <div className="report-toolbar d-print-none">
          <span className="report-toolbar-label">
            <span className="report-toolbar-label-dot" />
            التقرير جاهز للطباعة
          </span>
          <div className="report-toolbar-actions">
            <button type="button" className="btn-report-print" onClick={handlePrint}>
              <FaPrint /> طباعة / حفظ PDF
            </button>
          </div>
        </div>
      )}

      {/* ─── Weekly Filter ─── */}
      {activeTab === 'weekly' && (
        <>
          <div className="report-filter-card d-print-none">
            <div className="report-filter-head">
              <div className="report-filter-head-icon"><FaCalendarAlt /></div>
              <span className="report-filter-title">تقرير أسبوعي — اختر الفترة الزمنية</span>
            </div>
            <div className="report-filter-body">
              <form onSubmit={handleWeeklyReport}>
                <div className="report-filter-row">
                  <div className="report-filter-group">
                    <label className="report-filter-label">من تاريخ</label>
                    <Form.Control type="date" value={weeklyStart} onChange={(e) => setWeeklyStart(e.target.value)} required />
                  </div>
                  <div className="report-filter-group">
                    <label className="report-filter-label">إلى تاريخ</label>
                    <Form.Control type="date" value={weeklyEnd} onChange={(e) => setWeeklyEnd(e.target.value)} required />
                  </div>
                  <div className="report-filter-submit-wrap">
                    <button type="submit" className="report-filter-submit" disabled={loading}>
                      {loading ? <><span className="spin-inline" />جاري التحميل...</> : <><FaSearch /> عرض التقرير</>}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          {weeklyData ? (
            <div className="report-viewer">
              <div className="report-viewer-chrome d-print-none">
                <div className="report-viewer-chrome-dots">
                  <span /><span /><span />
                </div>
                <div className="report-viewer-chrome-label">
                  <FaChartBar style={{ fontSize: '.75rem' }} /> معاينة التقرير
                </div>
              </div>
              <div className="report-viewer-paper">
                {renderWeekly()}
              </div>
            </div>
          ) : !loading && (
            <div className="report-viewer d-print-none">
              <div className="report-empty-state">
                <div className="report-empty-icon"><FaCalendarAlt /></div>
                <div className="report-empty-title">لم يتم تحميل التقرير بعد</div>
                <div className="report-empty-hint">اختر الفترة الزمنية واضغط «عرض التقرير» لتحميل البيانات</div>
              </div>
            </div>
          )}
          {loading && (
            <div className="report-viewer d-print-none">
              <div className="report-loading">
                <div className="report-loading-spinner" />
                جاري تحميل التقرير...
              </div>
            </div>
          )}
        </>
      )}

      {/* ─── Monthly Filter ─── */}
      {activeTab === 'monthly' && (
        <>
          <div className="report-filter-card d-print-none">
            <div className="report-filter-head">
              <div className="report-filter-head-icon"><FaChartBar /></div>
              <span className="report-filter-title">تقرير شهري — اختر الشهر والسنة</span>
            </div>
            <div className="report-filter-body">
              <form onSubmit={handleMonthlyReport}>
                <div className="report-filter-row">
                  <div className="report-filter-group">
                    <label className="report-filter-label">السنة</label>
                    <Form.Select value={monthlyYear} onChange={(e) => setMonthlyYear(e.target.value)}>
                      {years.map(y => <option key={y} value={y}>{formatNumber(y)}</option>)}
                    </Form.Select>
                  </div>
                  <div className="report-filter-group">
                    <label className="report-filter-label">الشهر</label>
                    <Form.Select value={monthlyMonth} onChange={(e) => setMonthlyMonth(e.target.value)}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                        <option key={m} value={m.toString().padStart(2, '0')}>{getMonthName(m)}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="report-filter-submit-wrap">
                    <button type="submit" className="report-filter-submit" disabled={loading}>
                      {loading ? <><span className="spin-inline" />جاري...</> : <><FaSearch /> عرض</>}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          {monthlyData ? (
            <div className="report-viewer">
              <div className="report-viewer-chrome d-print-none">
                <div className="report-viewer-chrome-dots"><span /><span /><span /></div>
                <div className="report-viewer-chrome-label"><FaChartBar style={{ fontSize: '.75rem' }} /> معاينة التقرير</div>
              </div>
              <div className="report-viewer-paper">{renderMonthly()}</div>
            </div>
          ) : !loading && (
            <div className="report-viewer d-print-none">
              <div className="report-empty-state">
                <div className="report-empty-icon"><FaChartBar /></div>
                <div className="report-empty-title">لم يتم تحميل التقرير بعد</div>
                <div className="report-empty-hint">اختر الشهر والسنة ثم اضغط «عرض»</div>
              </div>
            </div>
          )}
          {loading && (
            <div className="report-viewer d-print-none">
              <div className="report-loading"><div className="report-loading-spinner" />جاري تحميل التقرير...</div>
            </div>
          )}
        </>
      )}

      {/* ─── Technician Performance Filter ─── */}
      {activeTab === 'technician' && (
        <>
          <div className="report-filter-card d-print-none">
            <div className="report-filter-head">
              <div className="report-filter-head-icon"><FaUserCog /></div>
              <span className="report-filter-title">أداء الفنيين — الفترة الزمنية (اختياري)</span>
            </div>
            <div className="report-filter-body">
              <form onSubmit={handleTechReport}>
                <div className="report-filter-row">
                  <div className="report-filter-group">
                    <label className="report-filter-label">السنة</label>
                    <Form.Select value={techYear} onChange={(e) => setTechYear(e.target.value)}>
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                        <option key={y} value={y}>{formatNumber(y)}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="report-filter-group">
                    <label className="report-filter-label">الشهر</label>
                    <Form.Select value={techMonth} onChange={(e) => setTechMonth(e.target.value)}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                        <option key={m} value={m.toString().padStart(2, '0')}>{getMonthName(m)}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="report-filter-submit-wrap">
                    <button type="submit" className="report-filter-submit" disabled={loading}>
                      {loading ? <><span className="spin-inline" />جاري التحميل...</> : <><FaSearch /> عرض التقرير</>}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          {techData ? (
            <div className="report-viewer">
              <div className="report-viewer-chrome d-print-none">
                <div className="report-viewer-chrome-dots"><span /><span /><span /></div>
                <div className="report-viewer-chrome-label"><FaUserCog style={{ fontSize: '.75rem' }} /> معاينة التقرير</div>
              </div>
              <div className="report-viewer-paper">{renderTech()}</div>
            </div>
          ) : !loading && (
            <div className="report-viewer d-print-none">
              <div className="report-empty-state">
                <div className="report-empty-icon"><FaUserCog /></div>
                <div className="report-empty-title">لم يتم تحميل التقرير بعد</div>
                <div className="report-empty-hint">حدد الفترة الزمنية (اختياري) واضغط «عرض التقرير»</div>
              </div>
            </div>
          )}
          {loading && (
            <div className="report-viewer d-print-none">
              <div className="report-loading"><div className="report-loading-spinner" />جاري تحميل التقرير...</div>
            </div>
          )}
        </>
      )}

      {/* ─── Project Report Filter ─── */}
      {activeTab === 'project' && (
        <>
          <div className="report-filter-card d-print-none">
            <div className="report-filter-head">
              <div className="report-filter-head-icon"><FaProjectDiagram /></div>
              <span className="report-filter-title">تقرير المشاريع — الفترة الزمنية (اختياري)</span>
            </div>
            <div className="report-filter-body">
              <form onSubmit={handleProjectReport}>
                <div className="report-filter-row">
                  <div className="report-filter-group">
                    <label className="report-filter-label">من تاريخ</label>
                    <Form.Control type="date" value={projectStart} onChange={(e) => setProjectStart(e.target.value)} />
                  </div>
                  <div className="report-filter-group">
                    <label className="report-filter-label">إلى تاريخ</label>
                    <Form.Control type="date" value={projectEnd} onChange={(e) => setProjectEnd(e.target.value)} />
                  </div>
                  <div className="report-filter-submit-wrap">
                    <button type="submit" className="report-filter-submit" disabled={loading}>
                      {loading ? <><span className="spin-inline" />جاري التحميل...</> : <><FaSearch /> عرض التقرير</>}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          {projectData ? (
            <div className="report-viewer">
              <div className="report-viewer-chrome d-print-none">
                <div className="report-viewer-chrome-dots"><span /><span /><span /></div>
                <div className="report-viewer-chrome-label"><FaProjectDiagram style={{ fontSize: '.75rem' }} /> معاينة التقرير</div>
              </div>
              <div className="report-viewer-paper">{renderProjectReport()}</div>
            </div>
          ) : !loading && (
            <div className="report-viewer d-print-none">
              <div className="report-empty-state">
                <div className="report-empty-icon"><FaProjectDiagram /></div>
                <div className="report-empty-title">لم يتم تحميل التقرير بعد</div>
                <div className="report-empty-hint">حدد الفترة (اختياري) واضغط «عرض التقرير»</div>
              </div>
            </div>
          )}
          {loading && (
            <div className="report-viewer d-print-none">
              <div className="report-loading"><div className="report-loading-spinner" />جاري تحميل التقرير...</div>
            </div>
          )}
        </>
      )}

      {/* ─── Overtime Report Filter ─── */}
      {activeTab === 'overtime' && (
        <>
          <div className="report-filter-card d-print-none">
            <div className="report-filter-head">
              <div className="report-filter-head-icon"><FaClock /></div>
              <span className="report-filter-title">تقرير العمل الإضافي — الفترة الزمنية (اختياري)</span>
            </div>
            <div className="report-filter-body">
              <form onSubmit={handleOvertimeReport}>
                <div className="report-filter-row">
                  <div className="report-filter-group">
                    <label className="report-filter-label">السنة</label>
                    <Form.Select value={overtimeYear} onChange={(e) => setOvertimeYear(e.target.value)}>
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                        <option key={y} value={y}>{formatNumber(y)}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="report-filter-group">
                    <label className="report-filter-label">الشهر</label>
                    <Form.Select value={overtimeMonth} onChange={(e) => setOvertimeMonth(e.target.value)}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                        <option key={m} value={m.toString().padStart(2, '0')}>{getMonthName(m)}</option>
                      ))}
                    </Form.Select>
                  </div>
                  <div className="report-filter-submit-wrap">
                    <button type="submit" className="report-filter-submit" disabled={loading}>
                      {loading ? <><span className="spin-inline" />جاري التحميل...</> : <><FaSearch /> عرض التقرير</>}
                    </button>
                    <button 
                      type="button" 
                      className="report-filter-submit ms-2" 
                      style={{ backgroundColor: '#28a745' }}
                      onClick={() => setShowOvertimeModal(true)}
                    >
                      <FaPlus /> إضافة عمل إضافي
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          {overtimeData ? (
            <div className="report-viewer">
              <div className="report-viewer-chrome d-print-none">
                <div className="report-viewer-chrome-dots"><span /><span /><span /></div>
                <div className="report-viewer-chrome-label"><FaClock style={{ fontSize: '.75rem' }} /> معاينة التقرير</div>
              </div>
              <div className="report-viewer-paper">{renderOvertimeReport()}</div>
            </div>
          ) : !loading && (
            <div className="report-viewer d-print-none">
              <div className="report-empty-state">
                <div className="report-empty-icon"><FaClock /></div>
                <div className="report-empty-title">لم يتم تحميل التقرير بعد</div>
                <div className="report-empty-hint">حدد الفترة (اختياري) واضغط «عرض التقرير»</div>
              </div>
            </div>
          )}
          {loading && (
            <div className="report-viewer d-print-none">
              <div className="report-loading"><div className="report-loading-spinner" />جاري تحميل التقرير...</div>
            </div>
          )}
        </>
      )}

      {/* ── Overtime Modal ── */}
      <Modal show={showOvertimeModal} onHide={() => {
        setShowOvertimeModal(false);
        setIsEditingOvertime(false);
        setEditOvertimeId(null);
      }} dir="rtl">
        <Modal.Header closeButton>
          <Modal.Title>{isEditingOvertime ? 'تعديل سجل عمل إضافي' : 'إضافة عمل إضافي يدوي (بدون تذكرة)'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveOvertime}>
            <Form.Group className="mb-3">
              <label className="form-label">الوصف / موضوع العمل</label>
              <Form.Control 
                type="text" 
                placeholder="مثال: صيانة أجهزة الفرع خارج الدوام"
                value={overtimeForm.description}
                onChange={(e) => setOvertimeForm({...overtimeForm, description: e.target.value})}
                required
              />
            </Form.Group>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <label className="form-label">التاريخ</label>
                  <Form.Control 
                    type="date" 
                    value={overtimeForm.overtime_date}
                    onChange={(e) => setOvertimeForm({...overtimeForm, overtime_date: e.target.value})}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <label className="form-label">الفرع (اختياري)</label>
                  <Form.Select 
                    value={overtimeForm.branch_id}
                    onChange={(e) => setOvertimeForm({...overtimeForm, branch_id: e.target.value})}
                  >
                    <option value="">اختر الفرع...</option>
                    {branches.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <label className="form-label">نوع العمل الإضافي</label>
                  <Form.Select 
                    value={overtimeForm.overtime_type}
                    onChange={(e) => setOvertimeForm({...overtimeForm, overtime_type: e.target.value})}
                    required
                  >
                    <option value="after_hours">بعد ساعات العمل الرسمية</option>
                    <option value="holiday">عطلة رسمية / عطلة عيد</option>
                    <option value="travel_over_100km">تنقل أكثر من 100 كم</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <label className="form-label">المدة (مثال: ساعتين)</label>
                  <Form.Control 
                    type="text" 
                    placeholder="اختياري لبعض الأنواع"
                    value={overtimeForm.overtime_duration}
                    onChange={(e) => setOvertimeForm({...overtimeForm, overtime_duration: e.target.value})}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <label className="form-label">الفنيين / المشاركين في العمل</label>
              <div className="tech-selection-box border rounded p-2" style={{ maxHeight: '150px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                <Row>
                  {assignableUsers.map(user => (
                    <Col md={6} key={user.id}>
                      <Form.Check 
                        type="checkbox"
                        id={`user-${user.id}`}
                        label={`${user.full_name} (${user.role === 'admin' ? 'مدير' : user.role === 'supervisor' ? 'مشرف' : 'فني'})`}
                        checked={overtimeForm.technician_ids.includes(user.id)}
                        onChange={() => handleTechToggle(user.id)}
                        className="mb-1"
                        style={{ fontSize: '0.85rem' }}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
              <Form.Text className="text-muted">اختر جميع الفنيين المشاركين في هذا العمل الإضافي.</Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end mt-3">
              <Button variant="secondary" onClick={() => setShowOvertimeModal(false)} className="me-2">
                إلغاء
              </Button>
              <Button variant="success" type="submit" disabled={loading}>
                {loading ? 'جاري الحفظ...' : (isEditingOvertime ? 'تحديث البيانات' : 'حفظ البيانات')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default Reports;
